import { Injectable } from '@angular/core';
import {
    ActivatedRouteSnapshot,
    BaseRouteReuseStrategy,
} from '@angular/router';

/**
 * Default route-reuse behavior, EXCEPT during a Gable-triggered "refresh the
 * page behind the drawer" navigation (SD-8019).
 *
 * A plain `router.navigateByUrl(sameUrl)` (even with `onSameUrlNavigation:
 * 'reload'`) reuses the routed component under Angular's default strategy, so
 * the page's `ngOnInit` never re-runs and stale data stays on screen. When the
 * drawer host commits a write it flips `forceReload` on for exactly one
 * navigation: `shouldReuseRoute` then returns false, Angular destroys +
 * recreates the routed component, and its existing `ngOnInit` fetch re-runs —
 * with zero changes to any page component.
 *
 * Normal navigations are untouched (identical to BaseRouteReuseStrategy), so
 * this is safe app-wide.
 */
@Injectable()
export class GableReloadRouteReuseStrategy extends BaseRouteReuseStrategy {
    private _forceReload = false;

    /** Force the NEXT navigation to re-create its routed components. */
    setForceReload(on: boolean): void {
        this._forceReload = on;
    }

    override shouldReuseRoute(
        future: ActivatedRouteSnapshot,
        curr: ActivatedRouteSnapshot,
    ): boolean {
        // Scope the forced re-create to the LEAF route only (the page
        // component the user is looking at). Reuse every ancestor — notably
        // V2LayoutComponent — so a Gable write doesn't re-run the layout's
        // loadAll/treasury-prefetch, scroll the page to top, or close open
        // dialogs (_dialog.closeAll()). The leaf is the snapshot with no
        // activated child; `curr === future` config guards to the same-url
        // refresh nav (all levels match) so nothing else is affected.
        if (
            this._forceReload &&
            future.routeConfig === curr.routeConfig &&
            future.firstChild === null
        ) {
            return false;
        }

        // A page that reads its id once (unit detail) is re-created when it links to a sibling id.
        if (
            future.routeConfig === curr.routeConfig &&
            future.routeConfig?.data?.['reloadOnParamChange'] &&
            JSON.stringify(future.params) !== JSON.stringify(curr.params)
        ) {
            return false;
        }

        return super.shouldReuseRoute(future, curr);
    }
}
